// US Pacific date/time helpers: the one place for the CRM's timezone rules.
// The database stores UTC instants. Every screen shows and accepts US Pacific time
// (America/Los_Angeles, PDT/PST), whatever timezone the user's PC is set to.
//
// Conversions go through Intl, which is exact on every PC. dayjs's own timezone maths is not:
// .startOf()/.endOf() on dayjs.tz objects can be an hour off on Pacific DST days, and on PCs
// whose own DST changes on other dates (Europe, Australia) dayjs.tz() can be an hour off for
// several hours around those changes.
//
// Tests: node scripts/test-usTime.mjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PT = 'America/Los_Angeles';

const WALL = 'YYYY-MM-DDTHH:mm:ss.SSS';
const DAY = 'YYYY-MM-DD';
const HOUR_MS = 60 * 60 * 1000;
// A date or date-time without an offset, e.g. '2026-09-01' or '2026-09-01 10:00:00'.
const NAIVE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)?$/;

const PT_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: PT,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const isEmpty = (v) => v === null || v === undefined || v === '';

// Pacific wall clock of an instant, as 'YYYY-MM-DDTHH:mm:ss.SSS'.
const ptWallOf = (ms) => {
  const p = {};
  PT_PARTS.formatToParts(new Date(ms)).forEach(({ type, value }) => {
    p[type] = value;
  });
  const millis = String(((ms % 1000) + 1000) % 1000).padStart(3, '0');
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}.${millis}`;
};

// Pacific wall clock -> UTC ms. The hour that repeats when DST ends resolves to its first
// occurrence (PDT); a time inside the skipped spring hour moves forward an hour, as Date does.
const ptWallToMs = (wall) => {
  const asUtc = dayjs.utc(wall);
  const pdt = asUtc.valueOf() + 7 * HOUR_MS;
  return ptWallOf(pdt) === asUtc.format(WALL) ? pdt : asUtc.valueOf() + 8 * HOUR_MS;
};

// Any stored value -> epoch ms, or null when empty/invalid. Instants (ISO string with Z or an
// offset, Date, epoch ms, dayjs) keep their moment; a string without an offset is read as
// Pacific wall time, the same way the backend reads it.
const instantMs = (v) => {
  if (isEmpty(v)) return null;
  if (typeof v === 'string' && NAIVE.test(v.trim())) {
    const wall = dayjs.utc(v.trim());
    return wall.isValid() ? ptWallToMs(wall.format(WALL)) : null;
  }
  const ms = dayjs(v).valueOf();
  return Number.isFinite(ms) ? ms : null;
};

const toIso = (ms) => new Date(ms).toISOString();

// ---------- Show: stored value -> US Pacific ----------

// formatPT(lead.createdAt) -> '09/27/2026 09:30 PM'. dayjs format tokens, except the zone
// tokens (Z, ZZ): use zoneAbbrPT() for 'PDT' / 'PST'.
export const formatPT = (v, fmt = 'MM/DD/YYYY hh:mm A', fallback = '—') => {
  const ms = instantMs(v);
  return ms === null ? fallback : dayjs.utc(ptWallOf(ms)).format(fmt);
};

export const formatPTDate = (v, fmt = 'MM/DD/YYYY', fallback = '—') => formatPT(v, fmt, fallback);

export const formatPTDateTime = (v, fmt = 'MM/DD/YYYY hh:mm A', fallback = '—') => formatPT(v, fmt, fallback);

// 'PDT' or 'PST' at that moment.
export const zoneAbbrPT = (v = new Date()) => {
  const ms = instantMs(v);
  if (ms === null) return 'PT';
  const part = new Intl.DateTimeFormat('en-US', { timeZone: PT, timeZoneName: 'short' })
    .formatToParts(new Date(ms))
    .find((p) => p.type === 'timeZoneName');
  return part ? part.value : 'PT';
};

// Drop-ins for Date#toLocaleString / toLocaleDateString / toLocaleTimeString, in US Pacific
// and en-US (the PC's language no longer changes the date order).
export const localeStringPT = (v, options = {}, locale = 'en-US') => {
  const ms = instantMs(v);
  return ms === null ? '' : new Date(ms).toLocaleString(locale, { ...options, timeZone: PT });
};

export const localeDatePT = (v, options = {}, locale = 'en-US') => {
  const ms = instantMs(v);
  return ms === null ? '' : new Date(ms).toLocaleDateString(locale, { ...options, timeZone: PT });
};

export const localeTimePT = (v, options = {}, locale = 'en-US') => {
  const ms = instantMs(v);
  return ms === null ? '' : new Date(ms).toLocaleTimeString(locale, { ...options, timeZone: PT });
};

// dayjs.tz object in US Pacific, for code that needs a dayjs. For display use formatPT:
// formatting a dayjs.tz object can be an hour off around the PC's own DST change (see top).
export const toPT = (v) => {
  const ms = instantMs(v);
  return ms === null ? null : dayjs(ms).tz(PT);
};

export const nowPT = (now = new Date()) => toPT(now);

// ---------- Today and offsets ----------

// Today's Pacific date, 'YYYY-MM-DD'.
export const todayPT = (now = new Date()) => ptWallOf(instantMs(now)).slice(0, 10);

export const isTodayPT = (v, now = new Date()) => {
  const ms = instantMs(v);
  return ms !== null && ptWallOf(ms).slice(0, 10) === todayPT(now);
};

// Pacific UTC offset in minutes at that moment: -420 (PDT) or -480 (PST).
export const ptOffsetMinutes = (now = new Date()) => {
  const ms = instantMs(now);
  return Math.round((dayjs.utc(ptWallOf(ms)).valueOf() - ms) / 60000);
};

// Minutes the PC's clock is ahead of US Pacific at that moment (negative when behind).
export const pcMinutesAheadOfPT = (now = new Date()) =>
  -new Date(instantMs(now)).getTimezoneOffset() - ptOffsetMinutes(now);

// 750 -> '12 h 30 min ahead of US Pacific', -180 -> '3 h behind US Pacific'.
export const formatGapFromPT = (minutesAhead) => {
  if (!minutesAhead) return 'same as US Pacific';
  const abs = Math.abs(minutesAhead);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const size = [hours ? `${hours} h` : '', minutes ? `${minutes} min` : ''].filter(Boolean).join(' ');
  return `${size} ${minutesAhead > 0 ? 'ahead of' : 'behind'} US Pacific`;
};

// ---------- Accept: antd DatePicker / RangePicker bridge ----------
// antd pickers work in the PC's timezone. Hand them a plain dayjs whose wall clock equals the
// Pacific wall clock (toPickerValue) and convert back when saving (fromPickerValue /
// fromPickerDate). Exact on India and US PCs; a PC whose own clock skips an hour on another
// date (Europe, Australia) cannot show that one hour.

export const toPickerValue = (v) => {
  const ms = instantMs(v);
  return ms === null ? null : dayjs(ptWallOf(ms));
};

// Pacific "now" as a picker value, for defaults.
export const nowPickerValue = (now = new Date()) => toPickerValue(now);

const asPick = (picked) => {
  if (isEmpty(picked)) return null;
  const d = dayjs(picked);
  return d.isValid() ? d : null;
};

// Date + time picker value -> UTC ISO string for the API.
export const fromPickerValue = (picked) => {
  const p = asPick(picked);
  return p ? toIso(ptWallToMs(p.format(WALL))) : null;
};

// Date-only picker value -> the picked Pacific calendar day, 'YYYY-MM-DD'.
export const pickedDay = (picked) => {
  const p = asPick(picked);
  return p ? p.format(DAY) : null;
};

// Date-only picker value -> Pacific midnight of that day as UTC ISO
// (07:00Z in PDT, 08:00Z in PST), the convention stored dates already use.
export const fromPickerDate = (picked) => {
  const day = pickedDay(picked);
  return day ? toIso(ptWallToMs(day)) : null;
};

// antd disabledDate: block days after / before today's Pacific date.
export const disabledFuturePT = (current) => Boolean(current) && dayjs(current).format(DAY) > todayPT();

export const disabledPastPT = (current) => Boolean(current) && dayjs(current).format(DAY) < todayPT();

// Preset ranges on the Pacific calendar, as picker values [start, end].
export const rangePT = (key, now = new Date()) => {
  const n = nowPickerValue(now);
  const whole = (d, unit) => [d.startOf(unit), d.endOf(unit)];
  switch (key) {
    case 'today':
      return whole(n, 'day');
    case 'yesterday':
      return whole(n.subtract(1, 'day'), 'day');
    case 'last7Days':
      return [n.subtract(6, 'day').startOf('day'), n.endOf('day')];
    case 'last30Days':
      return [n.subtract(29, 'day').startOf('day'), n.endOf('day')];
    case 'thisWeek':
      return whole(n, 'week');
    case 'lastWeek':
      return whole(n.subtract(1, 'week'), 'week');
    case 'thisMonth':
      return whole(n, 'month');
    case 'lastMonth':
      return whole(n.subtract(1, 'month'), 'month');
    case 'thisYear':
      return whole(n, 'year');
    default:
      throw new Error(`rangePT: unknown preset "${key}"`);
  }
};

// ---------- Filters and file names ----------

// Range picker value -> ['YYYY-MM-DD', 'YYYY-MM-DD'] Pacific days. leadGetAll, ReviewLead and
// Dispute read plain days as Pacific days.
export const toApiDays = (range) => {
  const first = pickedDay(range?.[0]);
  const last = pickedDay(range?.[1]);
  return first && last ? [first, last] : [];
};

// Range picker value -> [start, end] UTC ISO strings covering whole Pacific days, for endpoints
// that compare instants. Built from Pacific midnights, never from .startOf() on a dayjs.tz object.
export const toApiInstants = (range) => {
  const [first, last] = toApiDays(range);
  if (!first) return [];
  const dayAfterLast = dayjs.utc(last).add(1, 'day').format(DAY);
  return [toIso(ptWallToMs(first)), toIso(ptWallToMs(dayAfterLast) - 1)];
};

// Date for file names, e.g. `leads-${fileDatePT()}.xlsx`.
export const fileDatePT = (fmt = 'YYYY-MM-DD', now = new Date()) => formatPT(now, fmt);
